import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { useUserManagement } from '../../hooks/useUserManagement';
import {
  getAISystemOverview,
  transferOwnership as transferOwnershipRequest
} from '../../../services/AISystemsService';
import {
  fetchCurrentOverviewFailure,
  fetchCurrentOverviewStart,
  fetchCurrentOverviewSuccess
} from '../../../store/slices/aiSystemsSlice';
import { UserInfo } from '../../../types/AISystems.types';
import { TextButton } from '../../ui/Button/Buttons';
import { OwnershipTransferModal } from '../../ui/Modal/OwnershipTransferModal';
import { Tooltip } from '../../ui/Tooltip/Tooltip';
import { selectIsPlatformAdmin } from '../../../store/selectors/authSelectors';

type UsersProps = {
  data: {
    owner: UserInfo[];
    admin: UserInfo[];
    editor: UserInfo[];
    viewer: UserInfo[];
  };
};

export function Users({ data }: UsersProps) {
  const dispatch = useAppDispatch();
  const { id: aiSystemId } = useParams<{ id: string }>();
  const [showOwnershipTransferModal, setShowOwnershipTransferModal] =
    useState(false);
  const { fetchTeamUsers } = useUserManagement();

  const { user } = useAppSelector((state) => state.auth);
  const teamUsers = useAppSelector((state) => state.userManagement.teamUsers);
  const isAdminRole = useAppSelector(selectIsPlatformAdmin);
  const isOwner = user?.email === data.owner[0]?.email;
  const showTransferOwnership = isAdminRole || isOwner;
  const transferOwnershipText = isOwner
    ? 'You are currently the Owner of this AI system. Are you sure you want to transfer ownership to another user? By transferring ownership, you will automatically be reassigned as an Editor for this AI system.'
    : 'Are you sure you want to transfer ownership of this AI system? The selected user will become the new owner with full owner permissions, while the current owner will remain assigned as an Editor.';
  const ownerId = data.owner[0]?.id;
  const ownershipTransferUserOptions = (teamUsers ?? [])
    .filter((teamUser) => teamUser.id !== ownerId)
    .map((teamUser) => ({
      value: teamUser.email,
      label: teamUser.email
    }));

  useEffect(() => {
    if (!showOwnershipTransferModal) return;

    fetchTeamUsers();
  }, [showOwnershipTransferModal]);

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const renderUserAvatars = (
    users: UserInfo[],
    bgColor: string,
    textColor: string
  ) => {
    if (users.length === 0) return null;

    return users.map((user) => (
      <Tooltip
        key={user.id}
        position="top-right"
        content={
          <div className="flex flex-col">
            <span>
              {user.firstName} {user.lastName}
            </span>
            <span>{user.email}</span>
          </div>
        }
      >
        <div
          className={`w-8 h-8 rounded-full ${bgColor} flex items-center justify-center`}
        >
          <span
            className={`font-sans font-normal text-sm leading-[14px] text-center ${textColor}`}
          >
            {getInitials(user.firstName, user.lastName)}
          </span>
        </div>
      </Tooltip>
    ));
  };

  const transferOwnership = async (email: string) => {
    if (!aiSystemId) return;

    const nextOwner = (teamUsers ?? []).find(
      (teamUser) => teamUser.email.toLowerCase() === email.toLowerCase()
    );

    if (!nextOwner) {
      console.error('Failed to transfer ownership: user not found');
      return;
    }

    try {
      await transferOwnershipRequest(aiSystemId, nextOwner.id);
      dispatch(fetchCurrentOverviewStart());
      const updatedOverview = await getAISystemOverview(aiSystemId);
      dispatch(fetchCurrentOverviewSuccess(updatedOverview));
      setShowOwnershipTransferModal(false);
    } catch (error) {
      console.error('Failed to transfer ownership:', error);
      dispatch(fetchCurrentOverviewFailure('Failed to transfer ownership'));
    }
  };

  return (
    <div className="flex flex-col gap-6 text-left">
      <h2 className="font-sans font-medium text-base leading-6 text-gray-900v2">
        Users
      </h2>

      <div className="flex gap-6">
        {/* Owner */}
        {data.owner.length > 0 && (
          <div className="flex flex-col gap-2">
            <label className="font-sans font-medium text-xs leading-5 uppercase text-gray-550">
              Owner:
            </label>
            <div className="flex gap-2">
              {renderUserAvatars(
                data.owner,
                'bg-purple-100',
                'text-purple-700'
              )}
            </div>
          </div>
        )}

        {/* Admin */}
        {data.admin.length > 0 && (
          <div className="flex flex-col gap-2">
            <label className="font-sans font-medium text-xs leading-5 uppercase text-gray-550">
              Admin:
            </label>
            <div className="flex gap-2">
              {renderUserAvatars(data.admin, 'bg-gray-100', 'text-gray-800')}
            </div>
          </div>
        )}

        {/* Editor */}
        {data.editor.length > 0 && (
          <div className="flex flex-col gap-2">
            <label className="font-sans font-medium text-xs leading-5 uppercase text-gray-550">
              Editor:
            </label>
            <div className="flex gap-2">
              {renderUserAvatars(data.editor, 'bg-gray-100', 'text-gray-800')}
            </div>
          </div>
        )}

        {/* Viewer */}
        {data.viewer.length > 0 && (
          <div className="flex flex-col gap-2">
            <label className="font-sans font-medium text-xs leading-5 uppercase text-gray-550">
              Viewer:
            </label>
            <div className="flex gap-2">
              {renderUserAvatars(data.viewer, 'bg-gray-100', 'text-gray-800')}
            </div>
          </div>
        )}
      </div>
      {/* Hide for now */}
      {/* {showTransferOwnership && (
        <>
          <TextButton
            size="xs"
            onClick={() => setShowOwnershipTransferModal(true)}
            className="w-36"
          >
            Transfer ownership
          </TextButton>
          <OwnershipTransferModal
            onCancel={() => setShowOwnershipTransferModal(false)}
            onConfirm={transferOwnership}
            open={showOwnershipTransferModal}
            text={transferOwnershipText}
            userEmailOptions={ownershipTransferUserOptions}
          />
        </>
      )} */}
    </div>
  );
}
